import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Brand from "@/components/common/Brand";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { moreNavigation, primaryNavigation } from "./navigation";

const isActivePath = (pathname, path) =>
  pathname === path || pathname.startsWith(`${path}/`);

function NavItem({ path, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={path}
      onClick={onClick}
      className={({ isActive }) => `owner-nav-link ${isActive ? "active" : ""}`}
    >
      <Icon size={17} strokeWidth={1.7} />
      {label}
    </NavLink>
  );
}

export default function Sidebar() {
  const { currentUser } = useAuth();
  const { pathname } = useLocation();
  const moreActive = moreNavigation.some((item) => isActivePath(pathname, item.path));
  const [moreOpen, setMoreOpen] = useState(moreActive);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Phone tab bar: the first four destinations + More.
  const tabs = primaryNavigation.slice(0, 4);
  const tabMoreActive = !tabs.some((item) => isActivePath(pathname, item.path));

  return (
    <>
      <aside className="owner-sidebar">
        <Brand to="/dashboard" compact />
        <nav aria-label="Owner navigation" className="owner-nav">
          {primaryNavigation.map((item) => (
            <NavItem key={item.path} {...item} />
          ))}
          <button
            type="button"
            className={`owner-nav-link owner-nav-more ${moreActive ? "active-soft" : ""}`}
            onClick={() => setMoreOpen((value) => !value)}
            aria-expanded={moreOpen}
          >
            <MoreHorizontal size={17} strokeWidth={1.7} />
            More
            <ChevronDown size={15} className={`ml-auto transition-transform ${moreOpen ? "rotate-180" : ""}`} />
          </button>
          {moreOpen ? (
            <div className="owner-nav-sub">
              {moreNavigation.map((item) => (
                <NavItem key={item.path} {...item} />
              ))}
            </div>
          ) : null}
        </nav>
        <div className="sidebar-bottom">
          <Link to="/profile" className="sidebar-profile">
            <span>{currentUser?.name?.[0] || "H"}</span>
            <div>
              <strong>{currentUser?.name || "Owner"}</strong>
              <small>Settings</small>
            </div>
          </Link>
          <Link to="/" className="website-link">
            Rental website <ArrowUpRight size={14} />
          </Link>
        </div>
      </aside>

      <nav className="owner-tabbar" aria-label="Owner navigation">
        {tabs.map(({ path, label, icon: Icon }) => (
          <NavLink key={path} to={path} className={({ isActive }) => (isActive ? "active" : "")}>
            <Icon size={20} strokeWidth={1.8} />
            <span>{label}</span>
          </NavLink>
        ))}
        <button type="button" onClick={() => setSheetOpen(true)} className={tabMoreActive ? "active" : ""}>
          <MoreHorizontal size={20} strokeWidth={1.8} />
          <span>More</span>
        </button>
      </nav>

      <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <DialogContent className="owner-more-sheet">
          <DialogTitle>More</DialogTitle>
          <DialogDescription className="sr-only">Other pages in your workspace</DialogDescription>
          <div className="owner-more-grid">
            {[...primaryNavigation.slice(4), ...moreNavigation].map(({ path, label, icon: Icon }) => (
              <Link key={path} to={path} onClick={() => setSheetOpen(false)}>
                <Icon size={18} strokeWidth={1.8} />
                {label}
              </Link>
            ))}
            <Link to="/" onClick={() => setSheetOpen(false)}>
              <ArrowUpRight size={18} strokeWidth={1.8} />
              Rental website
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
